import { Button, Grid, Typography } from '@mui/material'
import { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useEventTracking, useIsManualMode, useUserSettings } from '../../../../../hooks'
import { Spacer } from '../../../../Spacer'
import { MergedAvailabilities } from '../../../../../types/mergedAvailabilities'
import { AppointmentFormData, AvailabilitiesResponse, EventData, RecommendationResource } from '../../../../../types'
import { Items } from './Items'
import { useTranslation } from 'react-i18next'
import { TimeSlotsSkeleton } from './TimeSlotsSkeleton'
import { ManualMode } from './ManualMode'
import { EventType } from '../../../../../constants/eventType'

interface Props {
  availabilities: MergedAvailabilities
  availabilityData?: AvailabilitiesResponse
  loading: boolean
  resource?: RecommendationResource | null
  durationInMinutes: number
  trackingData: EventData
}

interface DateTitleProps {
  formattedDate: string
}

const DateTitle = ({ formattedDate }: DateTitleProps) => {
  return (
    <Typography variant="h3" sx={{ pt: '0.25rem' }}>
      {formattedDate}
    </Typography>
  )
}

const HelperTitle = () => {
  const { t } = useTranslation()
  return (
    <Typography variant={'h3'} sx={{ pt: '0.25rem' }}>
      {t('common.label.pleaseSelectDay')}
    </Typography>
  )
}

const Container = ({ children }: React.PropsWithChildren<NonNullable<unknown>>) => (
  <Grid container spacing={3} sx={{ p: 0 }}>
    <Grid data-availabilities item xs={12}>
      {children}
    </Grid>
  </Grid>
)

const Helper = () => (
  <Container>
    <div>
      <HelperTitle />
    </div>
  </Container>
)

const AvailabilityItems = ({
  availabilities,
  loading,
  clientStartDate,
  hasSelectedADay,
}: Pick<Props, 'availabilities' | 'loading'> & { clientStartDate: Date; hasSelectedADay: boolean }) => {
  return <>{hasSelectedADay && (loading ? <TimeSlotsSkeleton /> : <Items availabilities={availabilities} dateStart={clientStartDate} />)}</>
}

const Time = ({ availabilities, availabilityData, loading, resource, durationInMinutes, trackingData }: Props) => {
  const { t } = useTranslation()
  const { locale } = useUserSettings()
  const { track: trackEvent } = useEventTracking()
  const { isManualMode, beManualMode } = useIsManualMode()
  const { watch, setValue } = useFormContext<AppointmentFormData>()
  const showManualTime = Boolean(resource)

  useEffect(() => {
    if (showManualTime) {
      trackEvent(EventType.MANUAL_TIME_BUTTON_DISPLAYED, { data: trackingData })
    }
  }, [showManualTime, trackEvent, trackingData])

  const switchManualMode = useCallback(() => {
    beManualMode((mm: boolean) => {
      const willBeManualMode = !mm
      // Making the value available on validation schema
      setValue('isManualMode', willBeManualMode)

      // Clear the opposite time field when switching modes
      if (willBeManualMode) {
        setValue('clientStartTime', undefined)
      } else {
        setValue('manualStartTime', undefined)
      }

      trackEvent(EventType.MANUAL_TIME_BUTTON_CLICKED, { data: trackingData })

      return willBeManualMode
    })
  }, [beManualMode, setValue, trackEvent, trackingData])

  const [clientStartDate] = watch(['clientStartDate']) as [Date]
  const hasSelectedADay = Boolean(clientStartDate)

  if (!hasSelectedADay) {
    return <Helper />
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }

  const formattedDate = clientStartDate ? clientStartDate?.toLocaleDateString(locale, dateOptions) : ''

  return (
    <Container>
      <div>
        <div>{hasSelectedADay ? <DateTitle formattedDate={formattedDate} /> : <HelperTitle />}</div>
      </div>
      {showManualTime && (
        <Button onClick={switchManualMode}>
          {t(isManualMode ? 'common.label.selectFromAvailabilities' : 'common.label.manuallyAddTime')}
        </Button>
      )}
      <Spacer />
      {isManualMode ? (
        <ManualMode
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          resource={resource!}
          durationInMinutes={durationInMinutes}
          availabilityData={availabilityData}
        />
      ) : (
        <AvailabilityItems
          availabilities={availabilities}
          loading={loading}
          clientStartDate={clientStartDate}
          hasSelectedADay={hasSelectedADay}
        />
      )}
    </Container>
  )
}

export { Time }
